import type React from 'react'
import { type InputHTMLAttributes, useEffect, useState } from 'react'
import { cn, cva } from '../lib/cva'
import { Text } from './Text'

type ToggleSize = 'small' | 'medium' | 'large'

interface ToggleStyleProps {
	disabled?: boolean
	checked?: boolean
	size?: ToggleSize
}

interface BaseToggleProps {
	label?: string
	description?: string
	labelPosition?: 'left' | 'right'
	/**
	 * Controlled checked state
	 */
	checked?: boolean
	/**
	 * Uncontrolled initial state
	 */
	defaultChecked?: boolean
	/**
	 * Extra callback for convenience: gets only the boolean checked value.
	 */
	onCheckedChange?: (checked: boolean) => void
}

export interface ToggleProps
	extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'onChange' | 'checked' | 'defaultChecked'>,
		ToggleStyleProps,
		BaseToggleProps {
	className?: string
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const styles = {
	wrapper: cva('flex items-center gap-3 cursor-pointer', {
		variants: {
			disabled: {
				true: 'cursor-not-allowed opacity-60',
				false: '',
			},
			labelPosition: {
				left: 'flex-row-reverse',
				right: 'flex-row',
			},
		},
		defaultVariants: {
			disabled: false,
			labelPosition: 'right',
		},
	}),
	track: cva(
		'relative inline-flex shrink-0 rounded-full transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2',
		{
			variants: {
				size: {
					small: 'h-5 w-9',
					medium: 'h-6 w-11',
					large: 'h-7 w-14',
				},
				checked: {
					true: 'bg-primary-600',
					false: 'bg-neutral-300',
				},
				disabled: {
					true: 'bg-neutral-200 cursor-not-allowed',
					false: '',
				},
			},
			compoundVariants: [
				{
					checked: true,
					disabled: true,
					className: 'bg-primary-300',
				},
			],
			defaultVariants: {
				size: 'medium',
				checked: false,
				disabled: false,
			},
		},
	),
	knob: cva(
		'pointer-events-none inline-block rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ease-in-out',
		{
			variants: {
				size: {
					small: 'h-4 w-4',
					medium: 'h-5 w-5',
					large: 'h-6 w-6',
				},
				checked: {
					true: '',
					false: 'translate-x-0.5',
				},
			},
			compoundVariants: [
				{
					size: 'small',
					checked: true,
					className: 'translate-x-[18px]',
				},
				{
					size: 'medium',
					checked: true,
					className: 'translate-x-[22px]',
				},
				{
					size: 'large',
					checked: true,
					className: 'translate-x-[30px]',
				},
				{
					size: 'small',
					checked: false,
					className: 'translate-x-0.5',
				},
				{
					size: 'medium',
					checked: false,
					className: 'translate-x-0.5',
				},
				{
					size: 'large',
					checked: false,
					className: 'translate-x-0.5',
				},
			],
			defaultVariants: {
				size: 'medium',
				checked: false,
			},
		},
	),
	labelContainer: cva('flex flex-col', {
		variants: {
			disabled: {
				true: 'text-neutral-400',
				false: 'text-neutral-800',
			},
		},
		defaultVariants: {
			disabled: false,
		},
	}),
}

export const Toggle: React.FC<ToggleProps> = ({
	className,
	size = 'medium',
	disabled = false,
	label,
	description,
	labelPosition = 'right',
	checked: controlledChecked,
	defaultChecked,
	onChange,
	onCheckedChange,
	...props
}) => {
	const [internalChecked, setInternalChecked] = useState<boolean>(controlledChecked ?? defaultChecked ?? false)

	// Keep internal state in sync when in controlled mode
	useEffect(() => {
		if (typeof controlledChecked === 'boolean') {
			setInternalChecked(controlledChecked)
		}
	}, [controlledChecked])

	const isControlled = typeof controlledChecked === 'boolean'
	const isChecked = isControlled ? (controlledChecked as boolean) : internalChecked

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return

		const newChecked = e.target.checked

		if (!isControlled) {
			setInternalChecked(newChecked)
		}

		onChange?.(e)
		onCheckedChange?.(newChecked)
	}

	const trackStyleProps: ToggleStyleProps = {
		disabled,
		checked: isChecked,
		size,
	}

	const knobStyleProps = {
		size,
		checked: isChecked,
	}

	return (
		<label className={cn(styles.wrapper({ disabled, labelPosition }), className)}>
			<span className={styles.track(trackStyleProps)}>
				<input
					type='checkbox'
					className='sr-only'
					disabled={disabled}
					checked={isChecked}
					onChange={handleChange}
					{...props}
				/>
				<span aria-hidden='true' className={cn(styles.knob(knobStyleProps), 'mt-0.5')} />
			</span>

			{(label || description) && (
				<span className={styles.labelContainer({ disabled })}>
					{label && (
						<Text
							variant='small'
							weight='semibold'
							className={cn('select-none', disabled ? 'text-neutral-400' : 'text-neutral-800')}>
							{label}
						</Text>
					)}

					{description && (
						<Text variant='xsmall' className='text-neutral-500 select-none'>
							{description}
						</Text>
					)}
				</span>
			)}
		</label>
	)
}
